import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { List, X, ArrowUpRight } from "@phosphor-icons/react";
import Logo from "./Logo";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/how-we-help", label: "How We Help" },
  { to: "/offerings", label: "Offerings" },
  { to: "/insights", label: "Insights" },
  { to: "/institutions", label: "For Institutions" },
  { to: "/about-nirmalya", label: "About Nirmalya" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background,box-shadow,padding] duration-500 ${
        scrolled ? "glass-nav py-3 shadow-[0_8px_30px_rgba(11,31,58,0.06)]" : "bg-transparent py-5"
      }`}
    >
      <div className="mx-auto max-w-[1320px] px-6 lg:px-10 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" aria-label="Founder+ home">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {LINKS.filter((l) => l.to !== "/institutions" && l.to !== "/contact").map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `relative whitespace-nowrap text-[15px] font-medium transition-colors duration-300 hover:text-navy ${
                  isActive ? "text-navy" : "text-slate-ink/70"
                }`
              }
            >
              {({ isActive }) => (
                <span className="relative">
                  {l.label}
                  {isActive && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute -bottom-2 left-0 h-[2px] w-full bg-gold"
                    />
                  )}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/institutions"
            data-testid="nav-institutions-btn"
            className="hidden sm:inline-flex whitespace-nowrap items-center gap-1.5 rounded-full bg-teal px-5 py-2.5 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5 hover:brightness-110"
          >
            For Institutions <ArrowUpRight size={16} weight="bold" />
          </Link>
          <Link
            to="/contact"
            data-testid="nav-cta-button"
            className="hidden sm:inline-flex whitespace-nowrap items-center gap-1.5 rounded-full bg-navy px-5 py-2.5 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5 hover:bg-navy-950"
          >
            Let's Connect <ArrowUpRight size={16} weight="bold" />
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden grid h-11 w-11 place-items-center rounded-full border border-slate-200 text-navy"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <List size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-white border-t border-slate-100"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `py-3 text-lg font-medium border-b border-slate-100 ${
                      isActive ? "text-gold" : "text-navy"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-nav-cta"
                className="mt-4 inline-flex justify-center rounded-full bg-navy px-5 py-3 font-semibold text-white"
              >
                Let's Connect
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
